import { useMemo, useState } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link, useNavigate, useParams } from "react-router-dom";
import Page from "../Components/Page";
import MainButton from "../Components/Buttons/MainButton";
import ModalShell, { ModalTitle } from "../Components/ModalShell";
import { axiosInstance, resolveFieldSoil } from "../Components/axiosFetchers";
import { geocodeAddress } from "../Components/googleMapsGeocoder";
import { useAuth } from "../Components/Auth/useAuth";
import { useClientConfig } from "../hooks/useClientConfig";
import { normalizeResolvedSoilResponse } from "../Components/resolvedSoilFieldValues";
import {
  GrowerSectionHeading,
  GrowerSurface,
  GrowerTableSurface,
} from "../Components/GrowerLayout.jsx";
import GhgQuickCalculatorUtility from "../Components/Utilities/GhgQuickCalculatorUtility.jsx";

const BULK_RESOLVER_ENDPOINT = "/utilities/slc-ecodistrict/resolve-csv";
const CSV_TEMPLATE_FILENAME = "slc-ecodistrict-resolver-template.csv";
const CSV_OUTPUT_FALLBACK_FILENAME = "slc-ecodistrict-results.csv";
const DEFAULT_CANADA_CENTER = {
  lat: 56.130366,
  lng: -106.346771,
};
const MAP_CONTAINER_STYLE = {
  width: "100%",
  height: "100%",
};

const CSV_TEMPLATE_CONTENT = [
  "farm_name,field_name,address,latitude,longitude,province",
  '"North Farm","Home Quarter","123 Example Rd, Brandon, MB",,,,MB',
  '"South Farm","Back 40","",50.121885,-95.650097,MB',
  '"Demo Farm","Needs Input","","","",""',
].join("\n");

const UTILITY_DEFINITIONS = [
  {
    id: "ghg-calculator",
    name: "GHG Quick Calculator",
    category: "Analysis",
    summary:
      "Calculate a transient Holos greenhouse gas estimate from location, crop, acres, and yield without adding Field Library records.",
    highlights: ["Quick form", "CSV rows", "No Field Library records"],
  },
  {
    id: "slc-ecodistrict-resolver",
    name: "SLC Polygon & Ecodistrict Resolver",
    category: "GIS",
    summary:
      "Resolve an address or coordinate pair into the Ecodistrict and SLC polygon values used by the platform.",
    highlights: [
      "Interactive map lookup",
      "Address and coordinate support",
      "Bulk CSV workflow scaffolded for backend processing",
    ],
  },
];

function createDownloadUrl(content, mimeType = "text/csv;charset=utf-8;") {
  const blob = new Blob([content], { type: mimeType });
  return URL.createObjectURL(blob);
}

function downloadFile(url, filename) {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function triggerTextDownload(content, filename) {
  const objectUrl = createDownloadUrl(content);

  try {
    downloadFile(objectUrl, filename);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function extractFilenameFromDisposition(dispositionHeader) {
  const match = dispositionHeader?.match(
    /filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i
  );

  if (!match?.[1]) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch (error) {
    return match[1];
  }
}

function parseCsvText(text) {
  const rows = [];
  let currentRow = [];
  let currentValue = "";
  let insideQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const nextChar = text[index + 1];

    if (char === '"') {
      if (insideQuotes && nextChar === '"') {
        currentValue += '"';
        index += 1;
      } else {
        insideQuotes = !insideQuotes;
      }
      continue;
    }

    if (char === "," && !insideQuotes) {
      currentRow.push(currentValue);
      currentValue = "";
      continue;
    }

    if ((char === "\n" || char === "\r") && !insideQuotes) {
      if (char === "\r" && nextChar === "\n") {
        index += 1;
      }

      currentRow.push(currentValue);
      if (currentRow.some((value) => String(value).trim() !== "")) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentValue = "";
      continue;
    }

    currentValue += char;
  }

  currentRow.push(currentValue);
  if (currentRow.some((value) => String(value).trim() !== "")) {
    rows.push(currentRow);
  }

  return rows;
}

function normalizeCsvHeader(header) {
  return String(header || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function buildBulkPreview(text) {
  const parsedRows = parseCsvText(text);

  if (parsedRows.length === 0) {
    throw new Error("The uploaded CSV is empty.");
  }

  const [rawHeaders, ...dataRows] = parsedRows;
  const headers = rawHeaders.map((header, index) =>
    String(header || "").trim() || `column_${index + 1}`
  );
  const normalizedHeaders = headers.map(normalizeCsvHeader);

  const rows = dataRows.map((rowValues, rowIndex) => {
    const record = {};
    const normalizedRecord = {};

    headers.forEach((header, index) => {
      record[header] = rowValues[index] ?? "";
    });

    normalizedHeaders.forEach((header, index) => {
      normalizedRecord[header] = String(rowValues[index] ?? "").trim();
    });

    const hasLatitude = normalizedRecord.latitude !== "";
    const hasLongitude = normalizedRecord.longitude !== "";
    const hasAddress = normalizedRecord.address !== "";

    let lookupSource = "None";
    let previewStatus = "Incomplete";
    let reason = "No coordinates data provided";

    if (hasLatitude && hasLongitude) {
      lookupSource = "Coordinates";
      previewStatus = "Ready";
      reason = "";
    } else if (hasAddress) {
      lookupSource = "Address";
      previewStatus = "Ready";
      reason = "";
    } else if (hasLatitude || hasLongitude) {
      lookupSource = "Partial coordinates";
      previewStatus = "Incomplete";
      reason = "Both latitude and longitude are required";
    }

    return {
      id: rowIndex + 1,
      record,
      lookupSource,
      previewStatus,
      reason,
    };
  });

  const readyRows = rows.filter((row) => row.previewStatus === "Ready").length;

  return {
    headers,
    rows,
    totalRows: rows.length,
    readyRows,
    incompleteRows: rows.length - readyRows,
  };
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-[8px] border border-slate-200 bg-white p-8 shadow-[0_18px_45px_rgba(15,23,42,0.05)]">
      <h2 className="text-[28px] font-black text-slate-900">
        {title}
      </h2>
      <p className="mt-3 max-w-[720px] text-[16px] leading-8 text-slate-600">
        {description}
      </p>
    </div>
  );
}

function StatusBanner({ tone = "neutral", children }) {
  const toneClasses = {
    neutral: "border-slate-200 bg-slate-50 text-slate-600",
    success: "border-[#cfe7d3] bg-[#eef8f0] text-[#2d6b37]",
    error: "border-[#f0c8c8] bg-[#fff2f2] text-[#8a2d2d]",
    info: "border-[#d5e7f3] bg-[#eef7fb] text-[#295d78]",
  };

  return (
    <div
      className={`rounded-[8px] border px-4 py-3 text-sm leading-6 ${
        toneClasses[tone] || toneClasses.neutral
      }`}
    >
      {children}
    </div>
  );
}

function ResultInfoModal({ onClose, onCopy, open, result, copied }) {
  if (!result) {
    return null;
  }

  const secondaryDetails = [
    { label: "Soil Code", value: result.soilCode },
    { label: "Province Code", value: result.provinceCode },
    { label: "Percent", value: result.percent },
    { label: "Cropped", value: result.cropped },
  ].filter((item) => item.value != null && String(item.value).trim() !== "");

  return (
    <Modal onClose={onClose} open={open}>
      <ModalShell
        accent="warning"
        contentClassName="overflow-auto"
        icon={<InfoOutlinedIcon sx={{ width: "60px", height: "60px", color: "#FFFFFF" }} />}
        onClose={onClose}
        width="min(980px, calc(100vw - 32px))"
      >
        <ModalTitle accent="warning">Resolver Result</ModalTitle>
        <p className="mb-5 text-slate-600">
          The selected location resolved successfully. You can copy these values to
          the clipboard and close when you&apos;re done.
        </p>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
            <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Ecodistrict
            </p>
            <p className="mt-3 text-[34px] font-black text-slate-900">
              {result.ecodistrict || "Not returned"}
            </p>
          </div>
          <div className="rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
            <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
              SLC Polygon
            </p>
            <p className="mt-3 text-[34px] font-black text-slate-900">
              {result.slcPolygon || "Not returned"}
            </p>
          </div>
        </div>

        {secondaryDetails.length > 0 ? (
          <div className="mt-4 rounded-[8px] border border-slate-200 bg-white p-5 shadow-[0_16px_30px_rgba(20,28,38,0.05)]">
            <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
              Returned Data
            </p>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {secondaryDetails.map((item) => (
                <div key={item.label}>
                  <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-slate-400">
                    {item.label}
                  </p>
                  <p className="mt-1 text-lg font-bold text-slate-900">
                    {String(item.value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-4 rounded-[8px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Source: {result.source}
          <br />
          Latitude: {result.lat}
          <br />
          Longitude: {result.lng}
        </div>

        <div className="mt-6 flex w-full justify-center gap-4">
          <MainButton
            onClick={onCopy}
            secondary={true}
            text={copied ? "Copied" : "Copy to Clipboard"}
          />
          <MainButton onClick={onClose} text="OK" />
        </div>
      </ModalShell>
    </Modal>
  );
}

function BulkUploadConfirmModal({
  bulkPreview,
  loading,
  onClose,
  onConfirm,
  open,
}) {
  if (!bulkPreview) {
    return null;
  }

  return (
    <Modal
      aria-describedby="bulk-upload-confirm-description"
      aria-labelledby="bulk-upload-confirm-title"
      onClose={onClose}
      open={open}
    >
      <ModalShell
        accent="warning"
        contentClassName="overflow-auto"
        icon={<InfoOutlinedIcon sx={{ width: "60px", height: "60px", color: "#FFFFFF" }} />}
        onClose={onClose}
        width="min(1100px, calc(100vw - 32px))"
      >
        <ModalTitle accent="warning" id="bulk-upload-confirm-title">
          CSV Preview & Confirm Upload
        </ModalTitle>
        <p className="mb-4 text-slate-600" id="bulk-upload-confirm-description">
          Review the uploaded rows below, then confirm to send this CSV to the bulk
          resolver endpoint.
        </p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <StatusBanner tone="info">Rows detected: {bulkPreview.totalRows}</StatusBanner>
          <StatusBanner tone="success">Ready rows: {bulkPreview.readyRows}</StatusBanner>
          <StatusBanner tone={bulkPreview.incompleteRows > 0 ? "error" : "neutral"}>
            Incomplete rows: {bulkPreview.incompleteRows}
          </StatusBanner>
        </div>

        <div className="mt-5 overflow-auto rounded-[8px] border border-slate-200">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-[#f5f5f5] text-slate-700">
              <tr>
                <th className="border-b border-slate-200 px-3 py-2 font-semibold">#</th>
                {bulkPreview.headers.map((header) => (
                  <th
                    className="border-b border-slate-200 px-3 py-2 font-semibold"
                    key={header}
                  >
                    {header}
                  </th>
                ))}
                <th className="border-b border-slate-200 px-3 py-2 font-semibold">
                  Lookup Source
                </th>
                <th className="border-b border-slate-200 px-3 py-2 font-semibold">
                  Preview Status
                </th>
                <th className="border-b border-slate-200 px-3 py-2 font-semibold">
                  Reason
                </th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {bulkPreview.rows.slice(0, 12).map((row) => (
                <tr className="bg-white even:bg-slate-50" key={row.id}>
                  <td className="border-b border-slate-100 px-3 py-2">{row.id}</td>
                  {bulkPreview.headers.map((header) => (
                    <td
                      className="border-b border-slate-100 px-3 py-2"
                      key={`${row.id}-${header}`}
                    >
                      {row.record[header] || "-"}
                    </td>
                  ))}
                  <td className="border-b border-slate-100 px-3 py-2">
                    {row.lookupSource}
                  </td>
                  <td className="border-b border-slate-100 px-3 py-2">
                    {row.previewStatus}
                  </td>
                  <td className="border-b border-slate-100 px-3 py-2">
                    {row.reason || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {bulkPreview.totalRows > 12 && (
          <p className="mt-3 text-sm text-slate-500">
            Showing the first 12 rows in the confirmation modal.
          </p>
        )}

        <div className="mt-6 flex w-full justify-center gap-4">
          <MainButton onClick={onConfirm} text={loading ? "Posting..." : "Confirm & Post"} />
          <MainButton onClick={onClose} secondary={true} text="Cancel" />
        </div>
      </ModalShell>
    </Modal>
  );
}

function UtilityCatalogView({ onOpenUtility, utilities }) {
  const groupedUtilities = useMemo(() => {
    return utilities.reduce((groups, utility) => {
      const nextGroups = { ...groups };
      nextGroups[utility.category] = [...(nextGroups[utility.category] || []), utility];
      return nextGroups;
    }, {});
  }, [utilities]);

  const categories = Object.entries(groupedUtilities);

  return (
    <div className="flex w-full flex-col gap-6 text-[#666666]">
      <GrowerSurface className="w-full">
        <GrowerSectionHeading
          actions={
            <div className="rounded-[8px] border border-slate-200 bg-white px-5 py-4 text-right shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
              <div className="text-[12px] font-bold uppercase text-slate-400">
                Utilities live
              </div>
              <div className="mt-2 text-4xl font-black text-slate-900">
                {utilities.length}
              </div>
            </div>
          }
          eyebrow="Utility Library"
          title="Explore field utilities"
          description="Open focused tools for quick analysis, lookup, validation, and GIS support outside the main farm workflow."
        />
      </GrowerSurface>

      {categories.map(([category, categoryUtilities]) => (
        <GrowerTableSurface
          className="w-full"
          key={category}
          meta={`${categoryUtilities.length} utility${
            categoryUtilities.length === 1 ? "" : "ies"
          }`}
          title={category}
        >
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            {categoryUtilities.map((utility) => (
              <button
                className="rounded-[8px] border border-slate-200 bg-white p-6 text-left shadow-[0_12px_28px_rgba(15,23,42,0.05)] transition hover:border-slate-400 hover:shadow-[0_16px_32px_rgba(15,23,42,0.08)]"
                key={utility.id}
                onClick={() => onOpenUtility(utility.id)}
                type="button"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="rounded-full bg-[#eef7fb] px-3 py-1 text-[11px] font-bold uppercase text-[#295d78]">
                    {utility.category}
                  </span>
                  <span className="rounded-full bg-[#f5ecff] px-3 py-1 text-[11px] font-bold uppercase text-[#8a32d6]">
                    Live
                  </span>
                </div>
                <h3 className="mt-5 text-[28px] font-black text-slate-900">
                  {utility.name}
                </h3>
                <p className="mt-4 text-[16px] leading-8 text-slate-600">
                  {utility.summary}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {utility.highlights.map((highlight) => (
                    <span
                      className="rounded-full border border-slate-200 px-3 py-1 text-sm font-semibold text-slate-600"
                      key={highlight}
                    >
                      {highlight}
                    </span>
                  ))}
                </div>
                <div className="mt-8 text-sm font-bold uppercase text-[#8a32d6]">
                  Open utility
                </div>
              </button>
            ))}
          </div>
        </GrowerTableSurface>
      ))}
    </div>
  );
}

function UtilityMapPane({
  className = "",
  googleMapsApiKey,
  mapAddress,
  mapStatus,
  mapError,
  mapCenter,
  mapMarker,
  onMapAddressChange,
  onSearchAddress,
  onMapClick,
}) {
  if (!googleMapsApiKey) {
    return (
      <StatusBanner tone="error">
        Google Maps configuration is missing, so the interactive map view is not
        available yet.
      </StatusBanner>
    );
  }

  return (
    <div
      className={`relative flex min-h-[780px] h-full flex-col overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_24px_50px_rgba(15,23,42,0.06)] ${className}`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-4 md:p-5">
        <div className="pointer-events-auto rounded-[8px] border border-white/70 bg-white/95 p-3 shadow-[0_18px_45px_rgba(15,23,42,0.12)] backdrop-blur">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="min-w-0 flex-1">
              <TextField
                fullWidth
                label="Search address"
                onChange={(event) => onMapAddressChange(event.target.value)}
                value={mapAddress}
              />
            </div>
            <MainButton
              disabled={mapStatus === "loading"}
              onClick={onSearchAddress}
              text={mapStatus === "loading" ? "Resolving..." : "Search"}
            />
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Search an address or click anywhere on the map to resolve that point.
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-[780px]">
        <GoogleMap
          center={mapCenter}
          mapContainerStyle={MAP_CONTAINER_STYLE}
          onClick={onMapClick}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            mapTypeId: "hybrid",
            rotateControl: false,
            streetViewControl: false,
            tilt: 0,
          }}
          zoom={mapMarker ? 14 : 4}
        >
          {mapMarker && <Marker position={mapMarker} />}
        </GoogleMap>
      </div>

      {(mapError || mapStatus === "loading") && (
        <div className="border-t border-slate-200 bg-white px-4 py-3">
          {mapStatus === "loading" && (
            <StatusBanner tone="info">Resolving selected location...</StatusBanner>
          )}
          {mapError && <StatusBanner tone="error">{mapError}</StatusBanner>}
        </div>
      )}
    </div>
  );
}

function UtilityCsvPane({
  className = "",
  bulkStatus,
  bulkMessage,
  selectedCsvFile,
  csvUploadInputKey,
  bulkPreviewError,
  bulkPreviewLoading,
  onDownloadTemplate,
  onFileChange,
}) {
  const csvTemplatePreview = useMemo(
    () => CSV_TEMPLATE_CONTENT.split("\n").slice(0, 3).join("\n"),
    []
  );

  return (
    <div
      className={`flex min-h-[780px] h-full flex-col rounded-[8px] border border-slate-200 bg-white p-6 shadow-[0_24px_50px_rgba(15,23,42,0.06)] ${className}`}
    >
      <h3 className="text-[24px] font-black text-slate-900">
        Bulk CSV resolver
      </h3>
      <p className="mt-2 text-[16px] leading-8 text-slate-600">
        Download the sample template, populate rows, and select a CSV to review it
        in a confirmation modal before posting.
      </p>

      <div className="mt-5">
        <StatusBanner tone="info">
          CSV rules:
          <br />
          1. Use `latitude` and `longitude` when both are present.
          <br />
          2. Otherwise, geocode `address`.
          <br />
          3. If neither is present, the row should return a row-level failure instead
          of aborting the full file.
        </StatusBanner>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <MainButton
          onClick={onDownloadTemplate}
          secondary={true}
          text="Download Sample CSV"
        />
      </div>

      <div className="mt-5 rounded-[8px] border border-slate-200 bg-[#fafafa] p-4 overflow-auto">
        <pre className="text-sm text-[#555555] whitespace-pre-wrap">
          {csvTemplatePreview}
        </pre>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <label className="text-sm text-[#666666]" htmlFor="csv-upload">
          Upload populated CSV
        </label>
        <input
          accept=".csv,text/csv"
          id="csv-upload"
          key={csvUploadInputKey}
          onChange={(event) => onFileChange(event.target.files?.[0] || null)}
          type="file"
        />
        {selectedCsvFile && (
          <p className="text-sm text-slate-500">Selected file: {selectedCsvFile.name}</p>
        )}
      </div>

      {bulkPreviewLoading && (
        <div className="mt-5">
          <StatusBanner tone="info">Reading CSV preview...</StatusBanner>
        </div>
      )}

      {bulkPreviewError && (
        <div className="mt-5">
          <StatusBanner tone="error">{bulkPreviewError}</StatusBanner>
        </div>
      )}

      {bulkMessage && (
        <div className="mt-5">
          <StatusBanner tone={bulkStatus === "success" ? "success" : "error"}>
            {bulkMessage}
          </StatusBanner>
        </div>
      )}
    </div>
  );
}

function UtilityDetailView({
  bulkMessage,
  bulkPreview,
  bulkPreviewError,
  bulkPreviewLoading,
  bulkStatus,
  csvUploadInputKey,
  googleMapsApiKey,
  mapAddress,
  mapCenter,
  mapError,
  mapMarker,
  mapStatus,
  onBack,
  onDownloadTemplate,
  onFileChange,
  onMapAddressChange,
  onMapClick,
  onMapSearch,
  selectedCsvFile,
  utility,
}) {
  return (
    <div className="min-h-full w-full bg-[#fbfbf8] px-4 py-5 md:px-8">
      <div className="mx-auto w-full max-w-[1480px]">
        <div className="rounded-[8px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(18,28,45,0.08)]">
          <section className="overflow-hidden rounded-t-[8px] border-b border-slate-200 bg-[linear-gradient(135deg,#fff7f0_0%,#ffffff_42%,#f4f7fb_100%)] px-6 py-7 md:px-10 md:py-9">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-[760px]">
                <div className="flex flex-wrap items-center gap-3 text-[12px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  <Link className="text-[#243245] hover:text-[#f15d22]" to="/utilities">
                    Utilities
                  </Link>
                  <span>&bull;</span>
                  <span>{utility.category}</span>
                  <span>&bull;</span>
                  <span>Live tool</span>
                </div>
                <h1 className="mt-5 text-[34px] font-black leading-tight text-slate-900 md:text-[56px]">
                  {utility.name}
                </h1>
                <p className="mt-4 max-w-[760px] text-[18px] leading-8 text-slate-600">
                  {utility.summary}
                </p>
              </div>
              <MainButton onClick={onBack} secondary={true} text="Back to utilities" />
            </div>
          </section>

          <div className="px-6 py-7 md:px-8 md:py-8">
            <div className="grid grid-cols-1 gap-6 items-stretch lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <UtilityMapPane
                className="lg:order-1"
                googleMapsApiKey={googleMapsApiKey}
                mapAddress={mapAddress}
                mapCenter={mapCenter}
                mapError={mapError}
                mapMarker={mapMarker}
                mapStatus={mapStatus}
                onMapAddressChange={onMapAddressChange}
                onMapClick={onMapClick}
                onSearchAddress={onMapSearch}
              />
              <UtilityCsvPane
                className="lg:order-2"
                bulkMessage={bulkMessage}
                bulkPreviewError={bulkPreviewError}
                bulkPreviewLoading={bulkPreviewLoading}
                bulkStatus={bulkStatus}
                csvUploadInputKey={csvUploadInputKey}
                onDownloadTemplate={onDownloadTemplate}
                onFileChange={onFileChange}
                selectedCsvFile={selectedCsvFile}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UtilitiesPage() {
  const { utilityId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config, loading } = useClientConfig();
  const [selectedCsvFile, setSelectedCsvFile] = useState(null);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkPreviewError, setBulkPreviewError] = useState("");
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("idle");
  const [bulkMessage, setBulkMessage] = useState("");
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [csvUploadInputKey, setCsvUploadInputKey] = useState(0);
  const [mapAddress, setMapAddress] = useState("");
  const [mapStatus, setMapStatus] = useState("idle");
  const [mapError, setMapError] = useState("");
  const [mapResult, setMapResult] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CANADA_CENTER);
  const [mapMarker, setMapMarker] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [copyStatus, setCopyStatus] = useState(false);

  const selectedUtility = useMemo(
    () => UTILITY_DEFINITIONS.find((utility) => utility.id === utilityId) || null,
    [utilityId]
  );

  document.title = selectedUtility
    ? `${selectedUtility.name} - Field To Market Canada`
    : "Utilities - Field To Market Canada";

  const googleMapsApiKey = config?.GOOGLE_APIKEY || "";

  async function resolveLookup(lat, lng, source) {
    const soilResolution = await resolveFieldSoil(
      {
        lat,
        lng,
      },
      user
    );
    const normalizedResolution = normalizeResolvedSoilResponse(soilResolution);

    return {
      lat,
      lng,
      source,
      ecodistrict: normalizedResolution.ecodistrict ?? "",
      slcPolygon: normalizedResolution.slcPolygon ?? "",
      soilCode: normalizedResolution.soilCode ?? "",
      provinceCode: normalizedResolution.provinceCode ?? "",
      percent: normalizedResolution.percent ?? "",
      cropped: normalizedResolution.cropped ?? "",
      rawResponse: soilResolution,
    };
  }

  function openResultModal(result) {
    setMapResult(result);
    setCopyStatus(false);
    setResultModalOpen(true);
  }

  async function handleMapAddressSearch() {
    if (!mapAddress.trim()) {
      setMapStatus("error");
      setMapError("Enter an address before searching the map.");
      return;
    }

    try {
      setMapStatus("loading");
      setMapError("");
      const coordinates = await geocodeAddress(mapAddress);
      setMapCenter(coordinates);
      setMapMarker(coordinates);
      const result = await resolveLookup(
        coordinates.lat,
        coordinates.lng,
        "address search"
      );
      openResultModal(result);
      setMapStatus("success");
    } catch (error) {
      setMapStatus("error");
      setMapError(error?.message || "Unable to search the map for that address.");
    }
  }

  async function handleMapClick(event) {
    const coordinates = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    try {
      setMapStatus("loading");
      setMapError("");
      setMapMarker(coordinates);
      setMapCenter(coordinates);
      const result = await resolveLookup(
        coordinates.lat,
        coordinates.lng,
        "map pin"
      );
      openResultModal(result);
      setMapStatus("success");
    } catch (error) {
      setMapStatus("error");
      setMapError(error?.message || "Unable to resolve the dropped pin.");
    }
  }

  async function handleCopyResult() {
    if (!mapResult) {
      return;
    }

    const text = [
      `Ecodistrict: ${mapResult.ecodistrict || "Not returned"}`,
      `SLC Polygon: ${mapResult.slcPolygon || "Not returned"}`,
      mapResult.soilCode ? `Soil Code: ${mapResult.soilCode}` : null,
      mapResult.provinceCode ? `Province Code: ${mapResult.provinceCode}` : null,
      mapResult.percent ? `Percent: ${mapResult.percent}` : null,
      mapResult.cropped ? `Cropped: ${mapResult.cropped}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus(true);
    } catch (error) {
      setCopyStatus(false);
    }
  }

  function handleDownloadTemplate() {
    triggerTextDownload(CSV_TEMPLATE_CONTENT, CSV_TEMPLATE_FILENAME);
  }

  function resetBulkSelection({ keepMessage = false } = {}) {
    setSelectedCsvFile(null);
    setBulkPreview(null);
    setBulkPreviewError("");
    setBulkPreviewLoading(false);
    setBulkConfirmOpen(false);
    setCsvUploadInputKey((currentKey) => currentKey + 1);

    if (!keepMessage) {
      setBulkMessage("");
      setBulkStatus("idle");
    }
  }

  async function handleCsvFileChange(file) {
    setSelectedCsvFile(file);
    setBulkPreview(null);
    setBulkPreviewError("");
    setBulkConfirmOpen(false);
    setBulkMessage("");
    setBulkStatus("idle");

    if (!file) {
      return;
    }

    try {
      setBulkPreviewLoading(true);
      const text = await file.text();
      const nextPreview = buildBulkPreview(text);
      setBulkPreview(nextPreview);
      setBulkConfirmOpen(true);
    } catch (error) {
      setBulkPreviewError(
        error?.message || "We couldn't read that CSV file for preview."
      );
    } finally {
      setBulkPreviewLoading(false);
    }
  }

  function handleCloseBulkConfirmModal() {
    resetBulkSelection();
  }

  async function handleConfirmBulkUpload() {
    if (!selectedCsvFile) {
      resetBulkSelection();
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedCsvFile);

    setBulkStatus("loading");
    setBulkMessage("");
    setBulkConfirmOpen(false);

    try {
      const response = await axiosInstance.post(BULK_RESOLVER_ENDPOINT, formData, {
        headers: {
          token: user?.token ? `Bearer ${user.token}` : undefined,
          provider: user?.provider || "microsoft",
        },
        responseType: "blob",
      });

      const disposition = response.headers?.["content-disposition"];
      const filename =
        extractFilenameFromDisposition(disposition) || CSV_OUTPUT_FALLBACK_FILENAME;
      const blobUrl = URL.createObjectURL(response.data);

      try {
        downloadFile(blobUrl, filename);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }

      setBulkStatus("success");
      setBulkMessage("Resolved CSV downloaded successfully.");
      resetBulkSelection({ keepMessage: true });
    } catch (error) {
      setBulkStatus("error");
      setBulkMessage(
        error?.message ||
          "Bulk resolver request failed. Backend support may still be pending."
      );
      resetBulkSelection({ keepMessage: true });
    }
  }

  if (loading && utilityId !== "ghg-calculator") {
    return (
      <Page
        title="Utilities"
        headerBorderColor="border-[#b026ff]"
        headerVariant="hybrid"
        contentWidth="wide"
        padding="p-0"
        introContent={
          <p>
            Open utility tools for quick analysis, GIS lookup, validation, and
            support tasks that sit alongside the main farm workflow.
          </p>
        }
      >
        <div className="flex min-h-[400px] w-full items-center justify-center bg-[#fbfbf8] text-lg text-slate-500">
          Loading utilities...
        </div>
      </Page>
    );
  }

  return (
    <Page
      title="Utilities"
      headerBorderColor="border-[#b026ff]"
      headerVariant="hybrid"
      contentWidth={utilityId ? "wide" : "wide"}
      padding={utilityId ? "p-0" : "px-4 pb-6 pt-5 md:px-6 md:pb-8"}
      showQuickFacts={false}
      introContent={
        <p>
          Open utility tools for quick analysis, GIS lookup, validation, and
          support tasks that sit alongside the main farm workflow.
        </p>
      }
    >
      {!utilityId ? (
        <UtilityCatalogView
          onOpenUtility={(id) => navigate(`/utilities/${id}`)}
          utilities={UTILITY_DEFINITIONS}
        />
      ) : selectedUtility?.id === "ghg-calculator" ? (
        <GhgQuickCalculatorUtility onBack={() => navigate("/utilities")} />
      ) : selectedUtility ? (
        googleMapsApiKey ? (
          <LoadScript googleMapsApiKey={googleMapsApiKey}>
            <UtilityDetailView
              bulkMessage={bulkMessage}
              bulkPreview={bulkPreview}
              bulkPreviewError={bulkPreviewError}
              bulkPreviewLoading={bulkPreviewLoading}
              bulkStatus={bulkStatus}
              csvUploadInputKey={csvUploadInputKey}
              googleMapsApiKey={googleMapsApiKey}
              mapAddress={mapAddress}
              mapCenter={mapCenter}
              mapError={mapError}
              mapMarker={mapMarker}
              mapStatus={mapStatus}
              onBack={() => navigate("/utilities")}
              onDownloadTemplate={handleDownloadTemplate}
              onFileChange={handleCsvFileChange}
              onMapAddressChange={setMapAddress}
              onMapClick={handleMapClick}
              onMapSearch={handleMapAddressSearch}
              selectedCsvFile={selectedCsvFile}
              utility={selectedUtility}
            />
          </LoadScript>
        ) : (
          <UtilityDetailView
            bulkMessage={bulkMessage}
            bulkPreview={bulkPreview}
            bulkPreviewError={bulkPreviewError}
            bulkPreviewLoading={bulkPreviewLoading}
            bulkStatus={bulkStatus}
            csvUploadInputKey={csvUploadInputKey}
            googleMapsApiKey={googleMapsApiKey}
            mapAddress={mapAddress}
            mapCenter={mapCenter}
            mapError={mapError}
            mapMarker={mapMarker}
            mapStatus={mapStatus}
            onBack={() => navigate("/utilities")}
            onDownloadTemplate={handleDownloadTemplate}
            onFileChange={handleCsvFileChange}
            onMapAddressChange={setMapAddress}
            onMapClick={handleMapClick}
            onMapSearch={handleMapAddressSearch}
            selectedCsvFile={selectedCsvFile}
            utility={selectedUtility}
          />
        )
      ) : (
        <div className="flex w-full bg-[#fbfbf8] p-8">
          <EmptyState
            description="The utility may have been removed, or the link might be out of date."
            title="Utility not found"
          />
        </div>
      )}

      <BulkUploadConfirmModal
        bulkPreview={bulkPreview}
        loading={bulkStatus === "loading"}
        onClose={handleCloseBulkConfirmModal}
        onConfirm={handleConfirmBulkUpload}
        open={bulkConfirmOpen}
      />
      <ResultInfoModal
        copied={copyStatus}
        onClose={() => setResultModalOpen(false)}
        onCopy={handleCopyResult}
        open={resultModalOpen}
        result={mapResult}
      />
    </Page>
  );
}
